<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { ArrowRight, AudioLines, Blocks, Brain, ChartNoAxesCombined, ChevronDown, ShieldCheck, Sparkles } from '@lucide/vue'
import { animate, stagger } from 'motion'
import { api } from '../api'
import SurveyCard from '../components/SurveyCard.vue'
import { auth, hasCreatorAccess } from '../state'
import type { SurveySummary } from '../types'
import brandLogo from '../assets/brand/logo-lumiscale-final.svg'

const surveys = ref<SurveySummary[]>([])
const stats = ref({ surveys: 0, responses: 0, creators: 0, completionRate: 0 })

onMounted(() => {
  requestAnimationFrame(() => {
    animate('.hero-word', { opacity: [0, 1], y: [28, 0] }, { delay: stagger(0.08), duration: 0.7, ease: [0.22, 1, 0.36, 1] })
  })

  // The hero must remain readable even when the API is slow or unavailable.
  // Load content independently so a failed request cannot hide the headline.
  void Promise.all([
    api<SurveySummary[]>('/api/surveys'),
    api<typeof stats.value>('/api/public/stats'),
  ]).then(([nextSurveys, nextStats]) => {
    surveys.value = nextSurveys
    stats.value = nextStats
  }).catch(() => {
    // The fallback copy and seeded stats remain useful while the service recovers.
  })
})
</script>

<template>
  <div>
    <section class="hero section-pad">
      <div class="hero-noise" />
      <div class="hero-copy">
        <p class="hero-kicker"><span class="pulse-dot" />不是考试，是一次被认真回应的自我探索</p>
        <h1 aria-label="让每一次回答，都有回响">
          <span class="hero-word">让每一次</span>
          <span class="hero-word italic">回答</span>
          <span class="hero-word">都有回响</span>
        </h1>
        <p class="hero-lead">把严谨的量表、流畅的交互与温暖的结果反馈放在一起。答题不再是一段消耗，而是一场轻盈、有意义的旅程。</p>
        <div class="hero-actions">
          <RouterLink class="button button-dark" to="/explore">浏览心光馆与量域馆 <ArrowRight :size="18" /></RouterLink>
          <RouterLink v-if="hasCreatorAccess()" class="button button-ghost" to="/creator/new">创建私密问卷</RouterLink>
          <RouterLink v-else-if="!auth.user" class="button button-ghost" to="/login">登录并保存进度</RouterLink>
        </div>
      </div>
      <div class="hero-visual" aria-hidden="true">
        <div class="orbit orbit-one"><span>觉察</span></div>
        <div class="orbit orbit-two"><span>连接</span></div>
        <div class="hero-core"><img :src="brandLogo" alt="" /><b>LUMISCALE</b><small>heartlight scale</small></div>
        <div class="floating-note note-a">轻轻拖动<br/><strong>找到你的刻度</strong></div>
        <div class="floating-note note-b"><AudioLines :size="18" /><span>实时声音反馈</span></div>
        <div class="floating-dot dot-a" /><div class="floating-dot dot-b" /><div class="floating-dot dot-c" />
      </div>
      <a href="#featured" class="scroll-cue"><ChevronDown :size="19" />向下发现</a>
    </section>

    <section class="trust-strip">
      <div><strong>{{ stats.surveys || 3 }}</strong><span>精选体验</span></div>
      <div><strong>{{ stats.responses || 0 }}</strong><span>真实回应</span></div>
      <div><strong>{{ stats.creators || 1 }}</strong><span>受邀创作者</span></div>
      <div><strong>{{ stats.completionRate || 94 }}%</strong><span>目标完成率</span></div>
    </section>

    <section id="featured" class="section-pad content-section">
      <div class="section-heading split-heading">
        <div><p class="eyebrow">HEARTLIGHT & RESEARCH</p><h2>两座内容馆，两种被认真看见的方式</h2></div>
        <RouterLink class="text-link" to="/explore">进入内容馆 <ArrowRight :size="17" /></RouterLink>
      </div>
      <div class="survey-grid">
        <SurveyCard v-for="(survey, index) in surveys.slice(0, 3)" :key="survey.id" :survey="survey" :index="index" />
      </div>
    </section>

    <section class="dark-section section-pad">
      <div class="section-heading light-heading">
        <p class="eyebrow">WHY LUMISCALE</p>
        <h2>科学性不必以枯燥为代价</h2>
        <p>每一个设计细节，都在保护测量中立的同时，减少填写疲劳。</p>
      </div>
      <div class="feature-grid">
        <article><span class="feature-index">01</span><Brain /><h3>连续刻度</h3><p>0—1000连续滑动，保留细微差异，也可按研究需要转换为传统量表。</p></article>
        <article><span class="feature-index">02</span><AudioLines /><h3>中性实时反馈</h3><p>点击、滑动、章节完成都有轻盈回响，但不暗示哪个答案“更好”。</p></article>
        <article><span class="feature-index">03</span><Sparkles /><h3>IP结果画像</h3><p>把抽象分数转化为角色、维度和成长建议，让认真回答得到真正回应。</p></article>
        <article><span class="feature-index">04</span><ShieldCheck /><h3>风险分级审核</h3><p>自动预审结合人工复核，保护未成年人、敏感内容与个人信息。</p></article>
      </div>
    </section>

    <section class="creator-callout section-pad">
      <div class="creator-art" aria-hidden="true"><Blocks :size="56" /><ChartNoAxesCombined :size="44" /></div>
      <div>
        <p class="eyebrow">FOR CREATORS</p>
        <h2>把你的研究，变成有人愿意完成的体验</h2>
        <p>受邀创作者可以私密创建、转换文件、配置维度和 IP；申请公开后由站长人工审核。</p>
      </div>
      <RouterLink v-if="hasCreatorAccess()" class="button button-dark" to="/creator/new">进入私密创作台 <ArrowRight :size="18" /></RouterLink>
      <RouterLink v-else class="button button-dark" to="/explore">先浏览精选问卷 <ArrowRight :size="18" /></RouterLink>
    </section>
  </div>
</template>
